import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import { JobMatchAnalysis, JobAnalysisScore } from 'components/JobMatchAnalysis';
import { Candidate, Job } from 'types/types';
import { BackstoryPageProps } from 'components/BackstoryTab';
import { useAuth } from 'hooks/AuthContext';
import { useSelectedCandidate, useSelectedJob } from 'hooks/GlobalContext';
import { CandidatePicker } from 'components/ui/CandidatePicker';
import { useNavigate, useParams } from 'react-router-dom';
import { ResumeEdit } from 'components/ui/ResumeEdit';
import { JobAnalysisStepper } from './job-analysis/JobAnalysisStepper';
import { JobDescriptionStep } from './job-analysis/JobDescriptionStep';
import {
  buildAnalysisPath,
  capitalize,
  getMissingStep,
  initializeFromParams,
  preserveAnalysisState,
} from './job-analysis/jobAnalysisInit';
import { AnalysisState, initialState } from './job-analysis/jobAnalysisTypes';

const JobAnalysisPage: React.FC<BackstoryPageProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, guest, apiClient } = useAuth();
  const { candidateId, jobId, stepId } = useParams<{
    candidateId?: string;
    jobId?: string;
    stepId?: string;
  }>();
  const { selectedCandidate, setSelectedCandidate } = useSelectedCandidate();
  const { selectedJob, setSelectedJob } = useSelectedJob();

  const [error, setError] = useState<string | null>(null);
  const [jobTab, setJobTab] = useState('select');
  const [analysisState, setAnalysisState] = useState<AnalysisState>(initialState);
  const [canAdvance, setCanAdvance] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(user === null ? 0 : 1);
  const maxStep = 4;

  useEffect(() => {
    initializeFromParams({
      candidateId,
      jobId,
      stepId,
      selectedCandidate,
      selectedJob,
      user,
      apiClient,
      activeStep,
      onSetCandidate: setSelectedCandidate,
      onSetJob: setSelectedJob,
      onSetActiveStep: setActiveStep,
      onSetError: setError,
      onSetAnalysisState: setAnalysisState,
      onSetInitialized: () => setIsInitialized(true),
    }).catch(() => {
      setError('Failed to initialize from URL parameters.');
      setIsInitialized(true);
    });
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    const newPath = buildAnalysisPath(
      selectedCandidate?.id || '',
      selectedJob?.id || '',
      activeStep
    );
    if (window.location.pathname !== newPath) navigate(newPath, { replace: true });
  }, [selectedCandidate, selectedJob, activeStep, isInitialized, navigate]);

  useEffect(() => {
    if (!isInitialized) return;
    setAnalysisState(prev => preserveAnalysisState(prev, selectedCandidate, selectedJob));
  }, [selectedCandidate, selectedJob, isInitialized]);

  useEffect(() => {
    if (!isInitialized) return;
    const isOwnProfile = user && selectedCandidate && user.id === selectedCandidate.id;
    if (!isOwnProfile || !selectedCandidate?.id || !selectedJob?.id) return;

    apiClient
      .getResumesByCandidate(selectedCandidate.id, selectedJob.id)
      .then(resumes => {
        const resumeId = resumes.length > 0 ? resumes[0].id ?? null : null;
        setAnalysisState(prev => ({ ...prev, savedResumeId: resumeId }));
      })
      .catch(() => undefined);
  }, [isInitialized, user, selectedCandidate, selectedJob, apiClient]);

  const getMissingStepRequirement = useCallback(
    (step: number) => getMissingStep(step, analysisState),
    [analysisState]
  );

  useEffect(() => {
    if (activeStep === maxStep) {
      setCanAdvance(false);
      return;
    }
    setCanAdvance(!getMissingStepRequirement(activeStep + 1));
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [analysisState, activeStep, getMissingStepRequirement]);

  const moveToStep = (step: number): void => {
    const missing = getMissingStepRequirement(step);
    if (missing) {
      setError(`${capitalize(missing)} is needed to access this step.`);
      return;
    }
    setActiveStep(step);
  };

  const onAnalysisComplete = useCallback(
    (analysis: JobAnalysisScore): void => {
      if (analysis.score !== analysisState.analysis?.score) {
        setAnalysisState(prev => ({ ...prev, analysis }));
      }
    },
    [analysisState.analysis?.score]
  );

  const handleResumeSaved = useCallback((savedResume: { id?: string }): void => {
    if (savedResume.id) {
      setAnalysisState(prev => ({ ...prev, savedResumeId: savedResume.id ?? null }));
    }
  }, []);

  if (!isInitialized) {
    return <Box>Loading...</Box>;
  }

  const renderAnalysis = (): JSX.Element => {
    if (!analysisState.job || !analysisState.candidate) {
      return (
        <Box>
          Missing required data:
          {!analysisState.candidate && ' candidate'}
          {!analysisState.job && ' job'}
        </Box>
      );
    }
    return (
      <JobMatchAnalysis
        variant="small"
        job={analysisState.job}
        candidate={analysisState.candidate}
        onAnalysisComplete={onAnalysisComplete}
      />
    );
  };

  return (
    <Box
      className="JobAnalysisPage"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        position: 'relative',
      }}
    >
      <JobAnalysisStepper
        activeStep={activeStep}
        isMobile={isMobile}
        selectedCandidate={selectedCandidate}
        selectedJob={selectedJob}
        analysisState={analysisState}
        theme={theme}
        moveToStep={moveToStep}
        savedResumeId={analysisState.savedResumeId}
      />
      <Box
        ref={scrollRef}
        sx={{
          position: 'relative',
          minHeight: 0,
          width: '100%',
          display: 'flex',
          flexGrow: 1,
          overflowY: 'auto',
          m: 0,
          p: 0,
        }}
      >
        {activeStep === 0 && (
          <CandidatePicker
            sx={{ pt: 1 }}
            onSelect={(candidate: Candidate): void => setSelectedCandidate(candidate)}
          />
        )}
        {activeStep === 1 && (
          <JobDescriptionStep
            jobTab={jobTab}
            user={user}
            guest={guest}
            selectedJob={selectedJob}
            onTabChange={(_, value): void => setJobTab(value)}
            onJobsSelected={(job: Job): void => setSelectedJob(job)}
          />
        )}
        {activeStep === 2 && renderAnalysis()}
        {activeStep === 3 && (
          <ResumeEdit
            resumeId={analysisState.savedResumeId ?? undefined}
            onSave={handleResumeSaved}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', p: 1 }}>
        <Button
          color="inherit"
          disabled={activeStep === 0}
          onClick={(): void => setActiveStep(step => step - 1)}
          sx={{ mr: 1 }}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === maxStep ? (
          <Button disabled={!canAdvance} onClick={(): void => moveToStep(0)} variant="outlined">
            Start New Analysis
          </Button>
        ) : (
          <Button
            disabled={!canAdvance}
            onClick={(): void => moveToStep(activeStep + 1)}
            variant="contained"
          >
            {activeStep === maxStep - 1 ? 'Done' : 'Next'}
          </Button>
        )}
      </Box>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={(): void => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={(): void => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export { JobAnalysisPage };
